export const NODE_WIDTH = 244;
export const NODE_HEIGHT = 96;
export const MIN_NODE_GAP = 48;
export const EDGE_CLEARANCE = 24;
export const EDGE_LANE_GAP = 18;

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nodeBox = (node) => ({
  id: node.id,
  x: toNumber(node.x),
  y: toNumber(node.y),
  w: NODE_WIDTH,
  h: NODE_HEIGHT,
});

export const nodeCenter = (node) => ({
  x: toNumber(node.x) + NODE_WIDTH / 2,
  y: toNumber(node.y) + NODE_HEIGHT / 2,
});

export const getConnectionPoint = (node, side) => {
  const x = toNumber(node.x);
  const y = toNumber(node.y);

  switch (side) {
    case "top":
      return { x: x + NODE_WIDTH / 2, y };
    case "bottom":
      return { x: x + NODE_WIDTH / 2, y: y + NODE_HEIGHT };
    case "left":
      return { x, y: y + NODE_HEIGHT / 2 };
    case "right":
    default:
      return { x: x + NODE_WIDTH, y: y + NODE_HEIGHT / 2 };
  }
};

export const nodesOverlap = (a, b, gap = MIN_NODE_GAP) => {
  const ax = toNumber(a.x);
  const ay = toNumber(a.y);
  const bx = toNumber(b.x);
  const by = toNumber(b.y);
  return (
    ax < bx + NODE_WIDTH + gap &&
    ax + NODE_WIDTH + gap > bx &&
    ay < by + NODE_HEIGHT + gap &&
    ay + NODE_HEIGHT + gap > by
  );
};

export const graphHasOverlaps = (nodes, gap = MIN_NODE_GAP) => {
  const list = nodes || [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (nodesOverlap(list[i], list[j], gap)) return true;
    }
  }
  return false;
};

export const findFreePosition = (x, y, nodes, ignoreId, gap = MIN_NODE_GAP) => {
  let nx = toNumber(x);
  let ny = toNumber(y);
  const others = (nodes || []).filter((node) => node.id !== ignoreId);

  for (let attempt = 0; attempt < 160; attempt += 1) {
    const hit = others.find((node) => nodesOverlap({ x: nx, y: ny }, node, gap));
    if (!hit) return { x: nx, y: ny };

    const hx = toNumber(hit.x);
    const hy = toNumber(hit.y);
    const centerDx = nx + NODE_WIDTH / 2 - (hx + NODE_WIDTH / 2);
    const centerDy = ny + NODE_HEIGHT / 2 - (hy + NODE_HEIGHT / 2);

    if (Math.abs(centerDx) >= Math.abs(centerDy)) {
      nx = centerDx >= 0 ? hx + NODE_WIDTH + gap : hx - NODE_WIDTH - gap;
    } else {
      ny = centerDy >= 0 ? hy + NODE_HEIGHT + gap : hy - NODE_HEIGHT - gap;
    }
  }

  return { x: nx, y: ny };
};

const samePositions = (left, right) =>
  left.length === right.length &&
  left.every((node, index) => {
    const other = right[index];
    return other && node.id === other.id && node.x === other.x && node.y === other.y;
  });

export const separateOverlappingNodes = (nodes, gap = MIN_NODE_GAP) => {
  const next = (nodes || []).map((node) => ({
    ...node,
    x: toNumber(node.x),
    y: toNumber(node.y),
  }));

  for (let iteration = 0; iteration < 80; iteration += 1) {
    let moved = false;
    for (let i = 0; i < next.length; i += 1) {
      for (let j = i + 1; j < next.length; j += 1) {
        if (!nodesOverlap(next[i], next[j], gap)) continue;

        const ac = nodeCenter(next[i]);
        const bc = nodeCenter(next[j]);
        const overlapX = NODE_WIDTH + gap - Math.abs(ac.x - bc.x);
        const overlapY = NODE_HEIGHT + gap - Math.abs(ac.y - bc.y);
        if (overlapX <= 0 || overlapY <= 0) continue;

        if (overlapX <= overlapY) {
          const dir = ac.x === bc.x ? (i < j ? 1 : -1) : ac.x > bc.x ? 1 : -1;
          const push = Math.max(1, Math.ceil(overlapX / 2));
          next[i] = { ...next[i], x: next[i].x + dir * push };
          next[j] = { ...next[j], x: next[j].x - dir * push };
        } else {
          const dir = ac.y === bc.y ? (i < j ? 1 : -1) : ac.y > bc.y ? 1 : -1;
          const push = Math.max(1, Math.ceil(overlapY / 2));
          next[i] = { ...next[i], y: next[i].y + dir * push };
          next[j] = { ...next[j], y: next[j].y - dir * push };
        }
        moved = true;
      }
    }
    if (!moved) break;
  }

  return samePositions(nodes || [], next) ? nodes : next;
};

export const correctModuleGraph = (graph) => {
  if (!graph || !Array.isArray(graph.nodes)) return graph;
  const nodes = separateOverlappingNodes(graph.nodes);
  if (nodes === graph.nodes) return graph;
  return { ...graph, nodes };
};

export const preferredSidesForEdge = (source, target) => {
  const sourceCenter = nodeCenter(source);
  const targetCenter = nodeCenter(target);
  const dx = targetCenter.x - sourceCenter.x;
  const dy = targetCenter.y - sourceCenter.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { fromSide: "right", toSide: "left" }
      : { fromSide: "left", toSide: "right" };
  }

  return dy >= 0
    ? { fromSide: "bottom", toSide: "top" }
    : { fromSide: "top", toSide: "bottom" };
};

const inflate = (box, pad) => ({
  ...box,
  x: box.x - pad,
  y: box.y - pad,
  w: box.w + pad * 2,
  h: box.h + pad * 2,
});

const segmentHitsBox = (x1, y1, x2, y2, box) => {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);
  return minX < box.x + box.w && maxX > box.x && minY < box.y + box.h && maxY > box.y;
};

const pointsToPath = (points) =>
  points.map((point, index) => `${index ? "L" : "M"} ${point.x} ${point.y}`).join(" ");

const pathSegments = (points) => {
  const segments = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    segments.push({
      x1: points[i].x,
      y1: points[i].y,
      x2: points[i + 1].x,
      y2: points[i + 1].y,
    });
  }
  return segments;
};

const pathLength = (points) =>
  pathSegments(points).reduce(
    (sum, segment) => sum + Math.abs(segment.x2 - segment.x1) + Math.abs(segment.y2 - segment.y1),
    0
  );

const labelFromPoints = (points) => {
  const segments = pathSegments(points);
  if (!segments.length) {
    return { labelX: points[0]?.x || 0, labelY: points[0]?.y || 0 };
  }
  const longest = segments.reduce((best, segment) => {
    const length = Math.abs(segment.x2 - segment.x1) + Math.abs(segment.y2 - segment.y1);
    return length > best.length ? { segment, length } : best;
  }, { segment: segments[0], length: 0 });
  return {
    labelX: (longest.segment.x1 + longest.segment.x2) / 2,
    labelY: (longest.segment.y1 + longest.segment.y2) / 2,
  };
};

const endPoint = (to, toSide) => ({
  x: toSide === "left" ? to.x - 6 : toSide === "right" ? to.x + 6 : to.x,
  y: toSide === "top" ? to.y - 6 : toSide === "bottom" ? to.y + 6 : to.y,
});

const simpleOrthogonalPoints = (from, to, fromSide, toSide, laneShift = 0) => {
  const end = endPoint(to, toSide);
  const horizontalDistance = Math.abs(end.x - from.x);
  const verticalDistance = Math.abs(end.y - from.y);
  const fromHorizontal = fromSide === "left" || fromSide === "right";
  const toHorizontal = toSide === "left" || toSide === "right";
  const spacing = 24;

  if (fromHorizontal && toHorizontal) {
    if (verticalDistance < 8) return [from, end];
    const midX = (from.x + end.x) / 2 + laneShift;
    return [from, { x: midX, y: from.y }, { x: midX, y: end.y }, end];
  }

  if (!fromHorizontal && !toHorizontal) {
    if (horizontalDistance < 8) return [from, end];
    const midY = (from.y + end.y) / 2 + laneShift;
    return [from, { x: from.x, y: midY }, { x: end.x, y: midY }, end];
  }

  if (fromHorizontal) {
    const fromOffsetX = from.x + (fromSide === "right" ? spacing : -spacing) + laneShift;
    return [from, { x: fromOffsetX, y: from.y }, { x: fromOffsetX, y: end.y }, end];
  }

  const fromOffsetY = from.y + (fromSide === "bottom" ? spacing : -spacing) + laneShift;
  return [from, { x: from.x, y: fromOffsetY }, { x: end.x, y: fromOffsetY }, end];
};

const aroundHorizontal = (from, to, fromSide, toSide, aroundY) => {
  const end = endPoint(to, toSide);
  const out = 24;
  const fromX = fromSide === "left" ? from.x - out : fromSide === "right" ? from.x + out : from.x;
  const toX = toSide === "left" ? end.x - out : toSide === "right" ? end.x + out : end.x;
  return [
    from,
    { x: fromX, y: from.y },
    { x: fromX, y: aroundY },
    { x: toX, y: aroundY },
    { x: toX, y: end.y },
    end,
  ];
};

const aroundVertical = (from, to, fromSide, toSide, aroundX) => {
  const end = endPoint(to, toSide);
  const out = 24;
  const fromY = fromSide === "top" ? from.y - out : fromSide === "bottom" ? from.y + out : from.y;
  const toY = toSide === "top" ? end.y - out : toSide === "bottom" ? end.y + out : end.y;
  return [
    from,
    { x: from.x, y: fromY },
    { x: aroundX, y: fromY },
    { x: aroundX, y: toY },
    { x: end.x, y: toY },
    end,
  ];
};

const between = (value, a, b, pad = 0) => {
  const min = Math.min(a, b) - pad;
  const max = Math.max(a, b) + pad;
  return value >= min && value <= max;
};

const boxesInCorridor = (from, to, boxes) => {
  const pad = EDGE_CLEARANCE + 8;
  const minX = Math.min(from.x, to.x) - pad;
  const maxX = Math.max(from.x, to.x) + pad;
  const minY = Math.min(from.y, to.y) - pad;
  const maxY = Math.max(from.y, to.y) + pad;
  return boxes.filter(
    (box) => box.x < maxX && box.x + box.w > minX && box.y < maxY && box.y + box.h > minY
  );
};

const pathHitCount = (points, boxes) =>
  pathSegments(points).reduce(
    (count, segment) =>
      count +
      boxes.filter((box) =>
        segmentHitsBox(segment.x1, segment.y1, segment.x2, segment.y2, inflate(box, EDGE_CLEARANCE))
      ).length,
    0
  );

const pathScore = (points, boxes) =>
  pathHitCount(points, boxes) * 1_000_000 + pathLength(points) + (points.length - 2) * 16;

export const buildOrthogonalPath = ({
  from,
  to,
  fromSide = "right",
  toSide = "left",
  obstacles = [],
  laneShift = 0,
}) => {
  const boxes = (obstacles || []).map((node) => nodeBox(node));
  const fromHorizontal = fromSide === "left" || fromSide === "right";
  const corridor = boxesInCorridor(from, to, boxes);
  const simple = simpleOrthogonalPoints(from, to, fromSide, toSide, laneShift);
  const candidates = [simple];
  const blockers = corridor.filter((box) =>
    pathSegments(simple).some((segment) =>
      segmentHitsBox(segment.x1, segment.y1, segment.x2, segment.y2, inflate(box, EDGE_CLEARANCE))
    )
  );

  blockers.forEach((box) => {
    if (fromHorizontal) {
      candidates.push(aroundHorizontal(from, to, fromSide, toSide, box.y - EDGE_CLEARANCE));
      candidates.push(aroundHorizontal(from, to, fromSide, toSide, box.y + box.h + EDGE_CLEARANCE));
      [box.x - EDGE_CLEARANCE, box.x + box.w + EDGE_CLEARANCE]
        .filter((midX) => between(midX, from.x, to.x, EDGE_CLEARANCE))
        .forEach((midX) => {
          candidates.push(
            simpleOrthogonalPoints(from, to, fromSide, toSide, midX - (from.x + to.x) / 2)
          );
        });
    } else {
      candidates.push(aroundVertical(from, to, fromSide, toSide, box.x - EDGE_CLEARANCE));
      candidates.push(aroundVertical(from, to, fromSide, toSide, box.x + box.w + EDGE_CLEARANCE));
      [box.y - EDGE_CLEARANCE, box.y + box.h + EDGE_CLEARANCE]
        .filter((midY) => between(midY, from.y, to.y, EDGE_CLEARANCE))
        .forEach((midY) => {
          candidates.push(
            simpleOrthogonalPoints(from, to, fromSide, toSide, midY - (from.y + to.y) / 2)
          );
        });
    }
  });

  const ranked = candidates
    .map((points) => ({ points, score: pathScore(points, corridor) }))
    .sort((a, b) => a.score - b.score);
  let chosen = ranked[0]?.points || simple;

  const simpleLength = pathLength(simple);
  const chosenLength = pathLength(chosen);
  if (simpleLength > 0 && chosenLength > simpleLength * 2.2) {
    chosen = simple;
  }

  const label = labelFromPoints(chosen);
  return {
    d: pointsToPath(chosen),
    labelX: label.labelX,
    labelY: label.labelY,
  };
};

export const edgeLaneShift = (edge, edges, nodesById) => {
  const source = nodesById[edge.source];
  const target = nodesById[edge.target];
  if (!source || !target) return 0;

  const { fromSide, toSide } = preferredSidesForEdge(source, target);
  const selfSource = nodeCenter(source);
  const selfTarget = nodeCenter(target);
  const peers = (edges || []).filter((other) => {
    const otherSource = nodesById[other.source];
    const otherTarget = nodesById[other.target];
    if (!otherSource || !otherTarget) return false;
    const sides = preferredSidesForEdge(otherSource, otherTarget);
    if (sides.fromSide !== fromSide || sides.toSide !== toSide) return false;
    const otherSourceCenter = nodeCenter(otherSource);
    const otherTargetCenter = nodeCenter(otherTarget);
    return (
      Math.abs(otherSourceCenter.y - selfSource.y) < NODE_HEIGHT &&
      Math.abs(otherTargetCenter.y - selfTarget.y) < NODE_HEIGHT
    );
  });

  if (peers.length < 2) return 0;
  const ordered = [...peers].sort((a, b) => String(a.id).localeCompare(String(b.id)));
  const index = ordered.findIndex((item) => item.id === edge.id);
  return (index - (ordered.length - 1) / 2) * EDGE_LANE_GAP;
};
